/**
 * src/lib/theme.js — dashboard mode selection (Phase 2 of the redesign).
 *
 * The dashboard now has exactly one theme, "Tattoo Labs" — REDESIGN.md's
 * Theme architecture collapses the old 5-theme picker down to a single
 * brand identity with a light/dark/system MODE choice instead. `data-theme`
 * on <html> is therefore always `'tattoo-labs'` here (styles.css's
 * generated block for it doubles as `:root`, so it's also correct with no
 * JS at all); this module only ever manages `data-mode`.
 *
 * Unrelated to the per-studio accent colour public pages inject — see
 * src/lib/studio-accent.js.
 */

const STORAGE_KEY = 'ts-theme'; // unchanged key: a returning user's stored
// value still resolves correctly via migrateLegacyValue() below, so nobody
// silently loses their preference across this redesign.

export const DASHBOARD_THEME = 'tattoo-labs';

/** Modes the dashboard mode picker offers, in picker order. */
export const MODES = ['light', 'dark', 'system'];

export const MODE_LABELS = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

/**
 * Maps a value written by the old 5-theme system (or any other unexpected
 * stored/API value) onto the new light/dark/system model, so a saved
 * preference from before this redesign still resolves to something sane
 * instead of erroring or silently falling back to the default.
 *
 * Mapping, per docs/redesign/theme-migration.md: `light` → light,
 * `dark`/`neon-ink`/`aurora` → dark (all three were dark-based themes),
 * `old-school-flash` → light (it was `color-scheme: light`). Anything else
 * (including `'system'`, already-migrated values, or genuinely unknown
 * input) passes through unchanged if it's a valid mode, else falls back to
 * `'system'`.
 *
 * @param {string|null|undefined} value
 * @returns {'light'|'dark'|'system'}
 */
export function migrateLegacyValue(value) {
  if (value === 'dark' || value === 'neon-ink' || value === 'aurora') return 'dark';
  if (value === 'light' || value === 'old-school-flash') return 'light';
  if (MODES.includes(value)) return value;
  return 'system';
}

/**
 * @returns {'light'|'dark'|'system'} The stored mode, migrated if it's a
 *   legacy value, or `'system'` if nothing is stored / storage is unavailable.
 */
export function getMode() {
  try {
    return migrateLegacyValue(localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'system';
  }
}

/**
 * Applies a mode to the document and persists it.
 *
 * @param {'light'|'dark'|'system'} mode
 * @returns {void}
 */
export function setMode(mode) {
  document.documentElement.dataset.theme = DASHBOARD_THEME;
  if (mode === 'system') {
    delete document.documentElement.dataset.mode;
  } else {
    document.documentElement.dataset.mode = mode;
  }
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // localStorage unavailable (private mode, disabled) — the mode still
    // applies for this session via the DOM attribute, it just won't persist.
  }
}

/** Applies whatever mode is already stored. Call once, as early as possible. */
export function applyStoredTheme() {
  setMode(getMode());
}

/**
 * Resolves live token values off `<html>` for embedding in a Stripe Element
 * — Elements render in a cross-origin iframe, so `var(--token)` itself
 * means nothing there; Stripe's `appearance.variables` needs real, resolved
 * colour/font strings. Read at mount time (not baked in statically) so an
 * embedded card form matches whichever mode — light, dark, or a studio's
 * own accent override — is actually active on the page around it.
 *
 * @param {string[]} names Token names without the leading `--`.
 * @returns {Record<string, string>}
 */
export function readThemeVars(names) {
  const style = getComputedStyle(document.documentElement);
  return Object.fromEntries(names.map((name) => [name, style.getPropertyValue(`--${name}`).trim()]));
}

/**
 * Builds a Stripe Elements `appearance` from the live dashboard tokens, so an
 * embedded card form reads like the app's own inputs in either mode.
 *
 * Stripe derives every colour it isn't given (input fill, borders, labels,
 * placeholders, tabs) from its base `theme`, so the base has to match the
 * page: `'night'` when the resolved `color-scheme` on <html> is dark,
 * `'stripe'` otherwise. Basing a dark page on the light `'flat'` theme is
 * what left the fields hard to read in dark mode. The `rules` then copy
 * styles.css's own `input`/`label` recipe onto Stripe's fields.
 *
 * @param {object} [opts]
 * @param {string} [opts.primary] Override for colorPrimary (a studio's
 *   accent on its public pages); defaults to the text colour, matching the
 *   dashboard's monochrome buttons.
 * @returns {object} A Stripe Elements `appearance` option.
 */
export function stripeElementsAppearance({ primary } = {}) {
  const vars = readThemeVars([
    'surface', 'text', 'text-muted', 'danger', 'font-sans',
    'control-border', 'control-fill-from', 'focus-ring', 'border-strong',
  ]);
  const dark = getComputedStyle(document.documentElement).getPropertyValue('color-scheme').includes('dark');
  return {
    theme: dark ? 'night' : 'stripe',
    variables: {
      colorPrimary: primary || vars.text,
      colorBackground: vars.surface,
      colorText: vars.text,
      colorTextSecondary: vars['text-muted'],
      colorTextPlaceholder: vars['text-muted'],
      colorDanger: vars.danger,
      borderRadius: '16px',
      fontFamily: vars['font-sans'],
      fontSizeBase: '14px',
    },
    rules: {
      '.Label': { color: vars['text-muted'] },
      '.Input': {
        backgroundColor: vars['control-fill-from'],
        border: `2px solid ${vars['control-border']}`,
        boxShadow: 'none',
        color: vars.text,
      },
      '.Input:focus': {
        borderColor: vars['focus-ring'],
        boxShadow: `0 0 0 1px ${vars['focus-ring']}`,
      },
      '.Input--invalid': { borderColor: vars.danger, color: vars.danger },
      '.Tab': {
        backgroundColor: vars['control-fill-from'],
        border: `2px solid ${vars['control-border']}`,
        color: vars['text-muted'],
      },
      '.Tab--selected': { borderColor: vars['border-strong'], color: vars.text },
      '.Block': { backgroundColor: vars['control-fill-from'], borderColor: vars['control-border'] },
    },
  };
}

/**
 * Calls `onChange` whenever the dashboard's resolved mode can have changed:
 * the picker flipping `data-mode`/`data-theme` on <html>, or — in system
 * mode — the OS colour scheme changing underneath the page. For embedded
 * Stripe UIs, which live in cross-origin iframes and can't see the page's
 * CSS, so their appearance has to be pushed again by hand.
 *
 * @param {() => void} onChange
 * @returns {() => void} Unsubscribe.
 */
export function onModeChange(onChange) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mode', 'data-theme'] });
  // Optional-chained: jsdom (the SPA tests) has no matchMedia.
  const media = window.matchMedia?.('(prefers-color-scheme: dark)');
  media?.addEventListener('change', onChange);
  return () => {
    observer.disconnect();
    media?.removeEventListener('change', onChange);
  };
}
